import React, { useCallback, useRef, useState } from 'react';
import ContentEditable from '../../utils/SafeContentEditable';
import { createBlock } from '../../utils/blockUtils';
import { FileUploadDropZone } from './components/FileUploadDropZone';
import { FileUploadFileRow } from './components/FileUploadFileRow';
import { FileUploadPendingRow } from './components/FileUploadPendingRow';
import { FileUploadPreview } from './components/FileUploadPreview';
import { useCrampedWidth } from './hooks/useCrampedWidth';
import { useFileUploadSlot } from './hooks/useFileUploadSlot';

/**
 * File upload field — one slot, one file.
 *
 * Schema lives on the Doc; the file lives on the mission. Need another
 * file? Add another /upload block. Library: edit the label only.
 * Reset campaign clears the mission value, not this block.
 */
export const FileUploadBlock = ({
  block,
  onUpdate,
  onInsertAfter,
  fileOverrides,
  onFilesChange,
  missionId,
  docId,
  compact = false,
}) => {
  const labelRef = useRef(null);
  const rootRef = useRef(null);
  const [previewFile, setPreviewFile] = useState(null);
  const cramped = useCrampedWidth(rootRef, compact);
  const {
    canUpload,
    files,
    pending,
    dragOver,
    dragHandlers,
    fileInputRef,
    openPicker,
    handlePick,
    handleRemove,
    handleDownload,
    dismissPending,
    retryPending,
  } = useFileUploadSlot({
    block,
    fileOverrides,
    onFilesChange,
    missionId,
    docId,
  });

  const commitLabel = useCallback(
    (html) => {
      onUpdate?.(block.id, { content: html });
    },
    [block.id, onUpdate]
  );

  const onLabelKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onInsertAfter?.(block.id, createBlock('text'));
    }
  };

  const uploading = pending.length > 0;
  const visibleFiles = uploading ? [] : files;
  const hasFiles = visibleFiles.length > 0 || uploading;

  return (
    <div
      ref={rootRef}
      className={`file-upload-block${cramped ? ' file-upload-block--widget' : ''}`}
    >
      <div className="file-upload-block__stage">
        {hasFiles ? (
          <ul className="file-upload-block__files">
            {visibleFiles.map((file) => (
              <FileUploadFileRow
                key={file.attachmentId}
                file={file}
                canUpload={canUpload}
                onView={setPreviewFile}
                onDownload={handleDownload}
                onReplace={openPicker}
                onRemove={handleRemove}
              />
            ))}
            {pending.map((job) => (
              <FileUploadPendingRow
                key={job.id}
                job={job}
                onRetry={retryPending}
                onDismiss={dismissPending}
              />
            ))}
          </ul>
        ) : (
          <FileUploadDropZone
            canUpload={canUpload}
            dragOver={dragOver}
            dragHandlers={dragHandlers}
            onOpenPicker={openPicker}
          />
        )}

        <ContentEditable
          innerRef={labelRef}
          html={block.content || ''}
          onChange={(e) => commitLabel(e.target.value)}
          onKeyDown={onLabelKeyDown}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className="file-upload-block__label doc-editable"
          tagName="div"
          placeholder="File upload"
        />
      </div>

      {canUpload ? (
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handlePick}
        />
      ) : null}

      {previewFile && missionId && !uploading ? (
        <FileUploadPreview
          missionId={missionId}
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      ) : null}
    </div>
  );
};
